import { CourseMaterial } from "./coursesLoader.ts";
import styles from "./StudentChoice.module.scss";
import { FormattedMessage, useIntl } from "react-intl";
import useVirtualStore from "./useVirtualStore.tsx";
import { clsx } from "clsx";
import { ReactNode, RefObject, useEffect, useMemo, useState } from "react";
import Tagline from "./common/Tagline.tsx";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import OffScreen from "../../../../common/core-ui/OffScreen.tsx";
import { Action } from "./useMaterialActions.tsx";
import { useFormattedVariant } from "../../../../common/features/product/useFormattedVariant.ts";
import {
  PrintVariant,
  Product,
  Variant,
} from "../../../../common/api/decimal/methods/product.ts";
import { createPortal } from "react-dom";
import LoaderButton from "../../../../common/core-ui/LoaderButton.tsx";
import { useFetcher } from "react-router-dom";
import { DecimalResult } from "../../../../common/api/decimal/utils.ts";
import useToasts from "../../../root/core-ui/toast/useToasts.ts";
import Toast from "../../../../common/core-ui/Toast.tsx";
import useStoreProduct, { FormatType } from "./useStoreProduct.tsx";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import StoreMarketplacePanel from "./StoreMarketplacePanel.tsx";
import {
  isOutOfStock,
  isPrintVariant,
  isTemporarilyOutOfStock,
} from "../../product/common/productUtils.ts";
import StoreStockStatusTag from "./StoreStockStatusTag.tsx";

function FormatDetail({
  title,
  count,
  isInstant,
}: {
  title: string;
  count: number;
  isInstant: boolean;
}) {
  return (
    <>
      {isInstant ? (
        <Tagline iconType={"lightning"}>
          <FormattedMessage defaultMessage={"Instant Access"} />
        </Tagline>
      ) : (
        <div />
      )}
      <div>
        <div className={styles.detailContainer}>
          <div className={styles.title}>{title}</div>
          <div className={styles.optionCount}>
            <FormattedMessage
              defaultMessage={
                "{optionCount, plural, =1 {1 option} other {# options}}"
              }
              values={{ optionCount: count }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

function VariantRadioButton({
  formatType,
  vbid,
  product,
  variant,
  selectedVal,
  onChange,
}: {
  formatType: FormatType;
  vbid: string;
  product: Product;
  variant: Variant;
  selectedVal: number;
  onChange: (id: number) => void;
}) {
  const checked = variant.id === selectedVal;
  const formattedVariant = useFormattedVariant(variant, product);
  const [panelOpenTime, setPanelOpenTime] = useState(0);

  useEffect(() => {
    if (!checked) {
      setPanelOpenTime(0);
    }
  }, [checked]);

  if (isPrintVariant(variant) && isOutOfStock(variant)) {
    // in my courses OOS items are hidden, this differs from main PDP
    return null;
  }

  return (
    <div className={styles.radioContainer}>
      <label
        className={clsx(
          "vst-radio-check vst-radio-check-option-radio-checkmark",
          "vst-radio-check-option-no-border",
          styles.radio
        )}
      >
        <input
          type="radio"
          name={"variantId"}
          value={variant.id}
          checked={checked}
          onChange={() => onChange(variant.id)}
        />
        <div>
          <span className={styles.captionTxt}>
            {formattedVariant.durationDescription}
          </span>
          <StoreStockStatusTag stockItem={variant} />
        </div>

        <div className={styles.price}>
          {variant.condition === "marketplace" && (
            <span className={styles.startingAt}>
              <FormattedMessage defaultMessage="starting at" />
            </span>
          )}
          {formattedVariant.longPrice}
        </div>

        {checked &&
          formatType === "digital" &&
          formattedVariant.expirationDate && (
            <div className={styles.expiringDate}>
              <FormattedMessage
                tagName={"div"}
                defaultMessage={
                  "Expires on: <strong>{expirationDate, date, medium}</strong>"
                }
                values={{
                  expirationDate: new Date(formattedVariant.expirationDate),
                  strong: (content) => <strong>{content}</strong>,
                }}
              />
            </div>
          )}
        {checked &&
          formatType === "print" &&
          isTemporarilyOutOfStock(variant) && (
            <div className={styles.stockDelay}>
              <FormattedMessage
                tagName={"div"}
                defaultMessage={"Usually ships within 1-2 weeks"}
              />
            </div>
          )}
      </label>
      {variant.condition === "marketplace" && (
        <>
          <BaseLinkOrButton
            className={clsx("vst-link", styles.marketplaceButton)}
            onClick={() => {
              if (!checked) {
                onChange(variant.id);
              }
              setPanelOpenTime(Date.now());
            }}
          >
            <FormattedMessage defaultMessage="Choose a Marketplace Option" />
          </BaseLinkOrButton>
          {panelOpenTime > 0 && (
            <StoreMarketplacePanel
              key={panelOpenTime}
              vbid={vbid}
              variant={variant}
              onRequestClose={() => setPanelOpenTime(0)}
            />
          )}
        </>
      )}
    </div>
  );
}

function VariantRadioGroup({
  formatType,
  vbid,
  product,
  selectedVal,
  onChange,
  disabled,
}: {
  formatType: FormatType;
  vbid: string;
  product: Product;
  selectedVal: number;
  onChange: (id: number) => void;
  disabled: boolean;
}) {
  const variants = product.variants;
  return (
    <fieldset disabled={disabled} className={styles.fieldset}>
      <OffScreen>
        {/* group context for the screen reader user */}
        <legend className={styles.caption}>
          {formatType === "digital" ? (
            <FormattedMessage
              defaultMessage={"Select the required digital license period."}
            />
          ) : (
            <FormattedMessage
              defaultMessage={"Select the required printed book format."}
            />
          )}
        </legend>
      </OffScreen>
      {variants.map((variant) => (
        <VariantRadioButton
          formatType={formatType}
          key={variant.id}
          vbid={vbid}
          product={product}
          variant={variant}
          selectedVal={selectedVal}
          onChange={onChange}
        />
      ))}
    </fieldset>
  );
}

function ChoiceWrapper({
  selectedVariant,
  selectedPrice,
  children,
  action,
  vbid,
  pricingRef,
  fetcher,
}: {
  selectedVariant?: Variant;
  selectedPrice: string;
  action: Action;
  children: ReactNode;
  vbid: string;
  pricingRef?: RefObject<HTMLDivElement>;
  fetcher: ReturnType<typeof useFetcher<DecimalResult<unknown>>>;
}) {
  const intl = useIntl();
  const { data, state } = fetcher;
  const { showToast } = useToasts();
  const [panelOpenTime, setPanelOpenTime] = useState(0);

  useEffect(() => {
    if (selectedVariant?.condition !== "marketplace") {
      setPanelOpenTime(0);
    }
  }, [selectedVariant?.condition]);

  useEffect(() => {
    if (data && !data.ok && state === "idle") {
      showToast(
        <Toast
          type="error"
          title={intl.formatMessage({
            defaultMessage: "We were unable to add this item to your cart.",
          })}
        />
      );
    }
  }, [data, intl, showToast, state]);

  const btnStyles = clsx("vst-button-pill", "vst-button", styles.actionButton);

  return (
    <>
      <fetcher.Form
        data-testid="student-choice"
        method="POST"
        action={`/my-courses/products/${vbid}`}
      >
        {/* this pertains to StudentChoice but is rendered elsewhere for aesthetics
          saves hoisting state to a component that has no interest/knowledge of it */}
        {pricingRef?.current &&
          selectedPrice &&
          createPortal(
            <div className={styles.selectedPrice}>{selectedPrice}</div>,
            pricingRef.current
          )}
        <input type="hidden" name="quantity" value="1" />
        {children}
        {selectedVariant?.condition === "marketplace" ? (
          <BaseLinkOrButton
            className={btnStyles}
            onClick={() => setPanelOpenTime(Date.now())}
          >
            <FormattedMessage defaultMessage="Choose a Marketplace Option" />
          </BaseLinkOrButton>
        ) : (
          <LoaderButton
            type={"submit"}
            loading={fetcher.state !== "idle"}
            aria-label={action.ariaLabel}
            className={btnStyles}
          >
            {action.label}
          </LoaderButton>
        )}
      </fetcher.Form>
      {panelOpenTime > 0 && selectedVariant && (
        <StoreMarketplacePanel
          key={panelOpenTime}
          vbid={vbid}
          variant={selectedVariant as PrintVariant}
          onRequestClose={() => setPanelOpenTime(0)}
        />
      )}
    </>
  );
}

// aka mini-PDP
export default function StudentChoice({
  material,
  action,
  pricingRef,
}: {
  material: CourseMaterial;
  action?: Action;
  // where to render selected price
  pricingRef?: RefObject<HTMLDivElement>;
}) {
  const intl = useIntl();
  const { isVirtualStore } = useVirtualStore();
  const { formatGroups, isOutOfStock } = useStoreProduct(material);
  const product = material.product;
  const fetcher = useFetcher();

  const [formatSelections, setFormatSelections] = useState<number[]>(() => {
    const selections = [];
    for (const format of formatGroups) {
      const variants = format.product.variants;
      selections.push(variants[0].id);
    }
    return selections;
  });
  const [selectedTab, setSelectedTab] = useState(0);

  const selectedVariant = formatGroups
    .map((group) => group.product.variants)
    .flat()
    .find((v) => v.id === formatSelections[selectedTab]);
  const [hasPricePortal, setHasPricePortal] = useState(false);
  useEffect(() => {
    // needed to update price when first mounted
    if (pricingRef?.current) {
      setHasPricePortal(true);
    }
  }, [pricingRef]);
  const selectedPrice = useMemo(() => {
    return hasPricePortal && selectedVariant?.studentPrice
      ? intl.formatNumber(parseFloat(selectedVariant.studentPrice), {
          style: "currency",
          currency: selectedVariant.studentCurrency,
        })
      : "";
  }, [hasPricePortal, intl, selectedVariant]);

  if (!isVirtualStore || !action || isOutOfStock) {
    return null;
  }

  if (!product || !formatGroups.length) {
    // if purchase options are not present this is handled by the caller
    // via the MaterialUnavailable component
    return null;
  }
  let content;
  if (formatGroups.length === 1) {
    const format = formatGroups[0];
    const product = format.product;
    const count = format.optionCount;
    content = (
      <>
        <div className={styles.singleHeader}>
          <FormatDetail
            title={format.title}
            count={count}
            isInstant={format.type === "digital" && product.kind === "book"}
          />
        </div>
        <VariantRadioGroup
          formatType={format.type}
          vbid={material.vbid}
          product={product}
          selectedVal={formatSelections[0]}
          onChange={(val) =>
            setFormatSelections({ ...formatSelections, [0]: val })
          }
          disabled={fetcher.state !== "idle"}
        />
      </>
    );
  } else {
    content = (
      <>
        <Tabs
          selectedIndex={selectedTab}
          onSelect={(index) => setSelectedTab(index)}
        >
          <TabList className={styles.dualHeader}>
            {formatGroups.map((format) => (
              <Tab key={format.title} className={styles.detailTab}>
                <FormatDetail
                  title={format.title}
                  count={format.optionCount}
                  isInstant={format.type === "digital"}
                />
              </Tab>
            ))}
          </TabList>

          {formatGroups.map((format, i) => (
            <TabPanel key={format.title}>
              {
                <VariantRadioGroup
                  formatType={format.type}
                  product={format.product}
                  vbid={material.vbid}
                  selectedVal={formatSelections[i]}
                  onChange={(val) =>
                    setFormatSelections({ ...formatSelections, [i]: val })
                  }
                  disabled={fetcher.state !== "idle"}
                />
              }
            </TabPanel>
          ))}
        </Tabs>
      </>
    );
  }
  return (
    <ChoiceWrapper
      selectedVariant={selectedVariant}
      selectedPrice={selectedPrice}
      pricingRef={pricingRef}
      action={action}
      vbid={material.vbid}
      fetcher={fetcher}
    >
      {content}
    </ChoiceWrapper>
  );
}
